/**
 * Login de demonstração da V2 (SPEC-v2 2.2, ADR-009). Mesma interface do `ApiAuthProvider` da
 * fase 2 (better-auth no VPS). Roda na thread principal, só com WebCrypto e IndexedDB.
 *
 * **Não protege nada contra quem abre o DevTools.** Se a demonstração for publicada antes da fase 2,
 * ela fica atrás de autenticação do servidor (no mínimo `auth_basic` do nginx).
 *
 * Regras: nenhuma senha no código (o dono cria a dele em `/configurar`); PBKDF2-SHA256 com 600.000
 * iterações; mensagem de erro sempre genérica; espera progressiva por e-mail; tokens de 256 bits
 * guardados só como SHA-256; convite de uso único com 72 h; sessão de 12 h (30 dias com "Manter
 * conectado"); step-up de 15 min.
 */
import type {
  AuthProvider,
  Id,
  InviteResult,
  Ms,
  Page,
  Role,
  SessionInfo,
  SignInResult,
  StudioFunction,
  SuiteAuditEntry,
  TeamMember,
} from '@rota/contracts';
import {
  type AuthContext,
  audit,
  browserTokenJar,
  CHALLENGE_MS,
  checkPasswordPolicy,
  currentSession,
  findByEmail,
  hasConfiguredOwner,
  type InviteRecord,
  type MemberRecord,
  memoryTokenJar,
  normEmail,
  openSession,
  providerError,
  RESET_MS,
  type ResetRecord,
  requireSession,
  revokeMemberSessions,
  STEP_UP_MS,
  sessionInfo,
  THROTTLE_FAILURES,
  THROTTLE_STEPS_MS,
  type ThrottleRecord,
  type TokenJar,
} from './core';
import { hashPassword, newToken, PBKDF2_ITERATIONS, sha256, verifyPassword, verifyTotp } from './crypto';
import { initialsOf, OWNER_ID } from './seed';
import { type AuthStorage, defaultStorage } from './storage';

export interface MockAuthOptions {
  storage?: AuthStorage;
  jar?: TokenJar;
  /** Só nos testes: menos iterações para a suíte rodar rápido. */
  iterations?: number;
  now?: () => Ms;
  origin?: string;
}

export interface RememberedRecord {
  id: Id;
  name: string;
  initials: string;
  email: string;
}

interface Challenge {
  memberId: Id;
  remember: boolean;
  expiresAt: Ms;
  attempts: number;
}

const DUMMY_SALT = 'cm90YS1kZW1vLWR1bW15';

function deviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Este aparelho';
  const ua = navigator.userAgent || '';
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Mac/.test(ua)
        ? 'Mac'
        : /Android/.test(ua)
          ? 'Android'
          : /Windows/.test(ua)
            ? 'Windows'
            : 'Navegador';
  const app = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : '';
  return app ? `${app} no ${os}` : os;
}

export class MockAuthProvider implements AuthProvider {
  readonly kind = 'mock' as const;
  readonly ctx: AuthContext;
  private readonly challenges = new Map<string, Challenge>();
  /** Último link de redefinição gerado (a demonstração não envia e-mail; só os testes leem). */
  lastResetLinkForTests: string | null = null;

  constructor(opts: MockAuthOptions = {}) {
    this.ctx = {
      storage: opts.storage ?? defaultStorage(),
      jar: opts.jar ?? (typeof window === 'undefined' ? memoryTokenJar() : browserTokenJar()),
      iterations: opts.iterations ?? PBKDF2_ITERATIONS,
      now: opts.now ?? (() => Date.now()),
      origin: opts.origin ?? (typeof location === 'undefined' ? 'http://localhost' : location.origin),
      device: deviceLabel(),
    };
  }

  /** Há um dono com senha neste navegador? Sem ele, o app abre `/configurar`. */
  isConfigured(): Promise<boolean> {
    return hasConfiguredOwner(this.ctx);
  }

  async session(): Promise<SessionInfo | null> {
    const cur = await currentSession(this.ctx);
    if (!cur) return null;
    const now = this.ctx.now();
    // Visto por último: no máximo uma escrita por minuto.
    if (now - cur.rec.lastSeenAt > 60_000) {
      await this.ctx.storage.put('sessions', cur.rec.key, { ...cur.rec, lastSeenAt: now });
      await this.ctx.storage.put('members', cur.member.id, { ...cur.member, lastSeenAt: now });
    }
    return sessionInfo(cur.member, cur.rec);
  }

  async setupOwner(i: { name: string; email: string; password: string }): Promise<SessionInfo> {
    if (await hasConfiguredOwner(this.ctx))
      throw providerError('conflict', 'O dono já foi configurado neste navegador.');
    const name = i.name.trim();
    const email = normEmail(i.email);
    if (!name) throw providerError('invalid', 'Informe o seu nome.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      throw providerError('invalid', 'Informe um e-mail válido.');
    checkPasswordPolicy(i.password);
    const now = this.ctx.now();
    const owner: MemberRecord = {
      id: OWNER_ID,
      name,
      initials: initialsOf(name),
      email,
      role: 'owner',
      functions: ['producer', 'compliance'],
      status: 'active',
      lastSeenAt: now,
      mfaEnrolled: false,
      password: await hashPassword(i.password, this.ctx.iterations),
      totp: null,
      createdAt: now,
    };
    await this.ctx.storage.put('members', owner.id, owner);
    const { seedTeam } = await import('./team');
    await seedTeam(this.ctx);
    await audit(this.ctx, owner.id, 'login_ok', 'Dono configurado neste navegador');
    return openSession(this.ctx, owner, false);
  }

  private async throttleOf(email: string): Promise<ThrottleRecord> {
    return (
      (await this.ctx.storage.get<ThrottleRecord>('throttle', normEmail(email))) ?? {
        failures: 0,
        level: 0,
        lockedUntil: 0,
      }
    );
  }

  private async fail(email: string, memberId: Id | null): Promise<SignInResult> {
    const t = await this.throttleOf(email);
    const failures = t.failures + 1;
    await audit(this.ctx, memberId ?? 'system', 'login_failed', 'Tentativa de entrada recusada');
    if (failures >= THROTTLE_FAILURES) {
      const wait = THROTTLE_STEPS_MS[Math.min(t.level, THROTTLE_STEPS_MS.length - 1)] ?? 60_000;
      const retryAt = this.ctx.now() + wait;
      await this.ctx.storage.put('throttle', normEmail(email), {
        failures: 0,
        level: t.level + 1,
        lockedUntil: retryAt,
      } satisfies ThrottleRecord);
      return { status: 'throttled', retryAt };
    }
    await this.ctx.storage.put('throttle', normEmail(email), { ...t, failures } satisfies ThrottleRecord);
    return { status: 'invalid' };
  }

  async signIn(i: { email: string; password: string; remember: boolean }): Promise<SignInResult> {
    if (!(await hasConfiguredOwner(this.ctx))) return { status: 'setup_required' };
    const t = await this.throttleOf(i.email);
    if (t.lockedUntil > this.ctx.now()) return { status: 'throttled', retryAt: t.lockedUntil };
    const m = await findByEmail(this.ctx, i.email);
    if (!m?.password || m.status !== 'active') {
      // Mesmo custo de uma senha errada: o tempo não revela se o e-mail existe.
      await verifyPassword(i.password, {
        algo: 'PBKDF2-SHA256',
        salt: DUMMY_SALT,
        iterations: this.ctx.iterations,
        hash: DUMMY_SALT,
      });
      return this.fail(i.email, null);
    }
    if (!(await verifyPassword(i.password, m.password))) return this.fail(i.email, m.id);
    await this.ctx.storage.delete('throttle', normEmail(i.email));
    if (m.totp) {
      const challengeId = newToken();
      this.challenges.set(challengeId, {
        memberId: m.id,
        remember: i.remember,
        expiresAt: this.ctx.now() + CHALLENGE_MS,
        attempts: 0,
      });
      return { status: 'totp_required', challengeId };
    }
    await audit(this.ctx, m.id, 'login_ok', i.remember ? 'Entrou e manteve conectado' : 'Entrou');
    if (i.remember) await this.rememberAccount(m);
    return { status: 'ok', session: await openSession(this.ctx, m, i.remember) };
  }

  private async rememberAccount(m: MemberRecord): Promise<void> {
    const list = (await this.ctx.storage.get<RememberedRecord[]>('meta', 'remembered')) ?? [];
    const next = [
      { id: m.id, name: m.name, initials: m.initials, email: m.email },
      ...list.filter((x) => x.id !== m.id),
    ];
    await this.ctx.storage.put('meta', 'remembered', next.slice(0, 4));
  }

  /**
   * Contas lembradas neste aparelho (quem marcou "Manter conectado"), para o login mostrar o avatar
   * (2.2). Ficam no IndexedDB do login, nunca no `localStorage`.
   */
  async rememberedAccounts(): Promise<RememberedRecord[]> {
    const list = (await this.ctx.storage.get<RememberedRecord[]>('meta', 'remembered')) ?? [];
    const out: RememberedRecord[] = [];
    for (const r of list) {
      const m = await this.ctx.storage.get<MemberRecord>('members', r.id);
      if (m && m.status === 'active' && m.password)
        out.push({ id: m.id, name: m.name, initials: m.initials, email: m.email });
    }
    return out;
  }

  async forgetAccount(id: Id): Promise<void> {
    const list = (await this.ctx.storage.get<RememberedRecord[]>('meta', 'remembered')) ?? [];
    await this.ctx.storage.put(
      'meta',
      'remembered',
      list.filter((x) => x.id !== id),
    );
  }

  /** Código do app autenticador ou um dos 10 códigos de recuperação (`xxxx-xxxx`, uso único). */
  private async checkSecondFactor(m: MemberRecord, code: string): Promise<MemberRecord | null> {
    if (!m.totp) return null;
    const clean = code.trim().toLocaleLowerCase('pt-BR');
    if (/^\d{6}$/.test(clean)) return (await verifyTotp(m.totp.secret, clean, this.ctx.now())) ? m : null;
    const h = await sha256(clean);
    if (!m.totp.recovery.includes(h)) return null;
    const next: MemberRecord = {
      ...m,
      totp: { ...m.totp, recovery: m.totp.recovery.filter((x) => x !== h) },
    };
    await this.ctx.storage.put('members', m.id, next);
    return next;
  }

  async verifyTotp(challengeId: Id, code: string): Promise<SessionInfo> {
    const c = this.challenges.get(challengeId);
    if (!c || c.expiresAt <= this.ctx.now())
      throw providerError('invalid', 'O código expirou. Entre de novo com a senha.');
    const m = await this.ctx.storage.get<MemberRecord>('members', c.memberId);
    const ok = m ? await this.checkSecondFactor(m, code) : null;
    if (!ok) {
      c.attempts++;
      if (c.attempts >= THROTTLE_FAILURES) this.challenges.delete(challengeId);
      await audit(this.ctx, c.memberId, 'login_failed', 'Código de verificação recusado');
      throw providerError('invalid', 'Código incorreto.');
    }
    this.challenges.delete(challengeId);
    await audit(this.ctx, ok.id, 'login_ok', 'Entrou com verificação em duas etapas');
    return openSession(this.ctx, ok, c.remember);
  }

  async stepUp(i: { password?: string; totp?: string }): Promise<SessionInfo> {
    const { rec, member } = await requireSession(this.ctx);
    let ok = false;
    if (i.password && member.password) ok = await verifyPassword(i.password, member.password);
    else if (i.totp) ok = (await this.checkSecondFactor(member, i.totp)) != null;
    if (!ok) {
      await audit(this.ctx, member.id, 'login_failed', 'Confirmação de identidade recusada');
      throw providerError('invalid', i.totp ? 'Código incorreto.' : 'Senha incorreta.');
    }
    const next = { ...rec, stepUpUntil: this.ctx.now() + STEP_UP_MS };
    await this.ctx.storage.put('sessions', rec.key, next);
    await audit(this.ctx, member.id, 'mfa', 'Identidade confirmada para uma ação sensível');
    return sessionInfo(member, next);
  }

  async signOut(): Promise<void> {
    const cur = await currentSession(this.ctx);
    if (cur) {
      await this.ctx.storage.delete('sessions', cur.rec.key);
      await audit(this.ctx, cur.member.id, 'logout', 'Saiu');
    }
    this.ctx.jar.clear();
  }

  async requestPasswordReset(email: string): Promise<void> {
    const m = await findByEmail(this.ctx, email);
    // Resposta sempre igual: não revela se o e-mail existe (10.2).
    if (m?.status !== 'active') return;
    const token = newToken();
    const now = this.ctx.now();
    const rec: ResetRecord = {
      key: await sha256(token),
      memberId: m.id,
      createdAt: now,
      expiresAt: now + RESET_MS,
      usedAt: null,
    };
    await this.ctx.storage.put('resets', rec.key, rec);
    this.lastResetLinkForTests = `${this.ctx.origin}/redefinir#t=${token}`;
    await audit(this.ctx, m.id, 'login_failed', 'Pediu redefinição de senha');
  }

  async resetPassword(token: string, password: string): Promise<void> {
    checkPasswordPolicy(password);
    const key = await sha256(token);
    const rec = await this.ctx.storage.get<ResetRecord>('resets', key);
    if (!rec || rec.usedAt != null || rec.expiresAt <= this.ctx.now())
      throw providerError('invalid', 'Este link de redefinição não vale mais. Peça um novo.');
    const m = await this.ctx.storage.get<MemberRecord>('members', rec.memberId);
    if (!m) throw providerError('not_found', 'Pessoa não encontrada.');
    await this.ctx.storage.put('members', m.id, {
      ...m,
      password: await hashPassword(password, this.ctx.iterations),
    } satisfies MemberRecord);
    await this.ctx.storage.put('resets', key, { ...rec, usedAt: this.ctx.now() });
    // Trocar a senha encerra todas as sessões (10.2).
    await revokeMemberSessions(this.ctx, m.id);
    await audit(this.ctx, m.id, 'session_revoked', 'Senha redefinida; sessões encerradas');
  }

  async acceptInvite(token: string, password: string, name?: string): Promise<SessionInfo> {
    const key = await sha256(token);
    const inv = await this.ctx.storage.get<InviteRecord>('invites', key);
    if (!inv || inv.usedAt != null || inv.expiresAt <= this.ctx.now())
      throw providerError('invalid', 'Este convite não vale mais. Peça um novo ao dono.');
    checkPasswordPolicy(password);
    const m = await this.ctx.storage.get<MemberRecord>('members', inv.memberId);
    if (!m) throw providerError('not_found', 'Convite sem pessoa.');
    const finalName = name?.trim() || m.name;
    const next: MemberRecord = {
      ...m,
      name: finalName,
      initials: initialsOf(finalName),
      role: inv.role,
      functions: [...inv.functions],
      status: 'active',
      password: await hashPassword(password, this.ctx.iterations),
    };
    await this.ctx.storage.put('members', m.id, next);
    await this.ctx.storage.put('invites', key, { ...inv, usedAt: this.ctx.now() });
    await revokeMemberSessions(this.ctx, m.id);
    await audit(this.ctx, m.id, 'invite', `Aceitou o convite como ${inv.role}`);
    return openSession(this.ctx, next, false);
  }

  async enrollTotp(): Promise<{ otpauthUrl: string; recoveryCodes: string[] }> {
    const { enrollTotp } = await import('./team');
    return enrollTotp(this.ctx);
  }

  /** Confirma o 2FA com o primeiro código do app autenticador (fora da interface; Conta › Segurança). */
  async confirmTotp(code: string): Promise<SessionInfo> {
    const { confirmTotp } = await import('./team');
    return confirmTotp(this.ctx, code);
  }

  /** Desliga o 2FA (exige step-up). */
  async disableTotp(): Promise<SessionInfo> {
    const { disableTotp } = await import('./team');
    return disableTotp(this.ctx);
  }

  /** Troca a própria senha (exige a atual). */
  async changePassword(current: string, next: string): Promise<void> {
    const { changePassword } = await import('./team');
    return changePassword(this.ctx, current, next);
  }

  /** Sessões da própria pessoa (Conta › Sessões). */
  async mySessions(): Promise<
    { id: string; device: string; createdAt: Ms; lastSeenAt: Ms; current: boolean; remembered: boolean }[]
  > {
    const { mySessions } = await import('./team');
    return mySessions(this.ctx);
  }

  async revokeMySession(id: string): Promise<void> {
    const { revokeMySession } = await import('./team');
    return revokeMySession(this.ctx, id);
  }

  /** Atualiza nome (Conta › Perfil). */
  async updateProfile(patch: { name: string }): Promise<SessionInfo> {
    const { updateProfile } = await import('./team');
    return updateProfile(this.ctx, patch);
  }

  /**
   * Sessão de demonstração sem senha (`?demo=<papel>`). **Só no dev server** (`import.meta.env.DEV`):
   * no build de produção a chamada é recusada. Ver ADR-009 e `data/auth/demo.ts`.
   */
  async demoSession(role: Role, opts: { allow: boolean }): Promise<SessionInfo> {
    if (!opts.allow) throw providerError('forbidden', 'Modo de demonstração desligado.');
    const { openDemoSession } = await import('./team');
    return openDemoSession(this.ctx, role);
  }

  readonly team = {
    list: async (): Promise<TeamMember[]> => (await import('./team')).listTeam(this.ctx),
    invite: async (i: {
      email: string;
      name: string;
      role: Role;
      functions: StudioFunction[];
    }): Promise<InviteResult> => (await import('./team')).invite(this.ctx, i),
    update: async (
      id: Id,
      patch: { role?: Role; functions?: StudioFunction[]; status?: 'active' | 'disabled' },
    ): Promise<TeamMember> => (await import('./team')).updateMember(this.ctx, id, patch),
    revokeSessions: async (id: Id): Promise<void> => (await import('./team')).revokeSessions(this.ctx, id),
    audit: async (q: {
      cursor?: string;
      limit: number;
      area?: 'painel' | 'automacoes' | 'estudio' | 'suite';
    }): Promise<Page<SuiteAuditEntry>> => (await import('./team')).listAudit(this.ctx, q),
    /** Convites pendentes (Equipe › Convites). Fora da interface. */
    pendingInvites: async () => (await import('./team')).pendingInvites(this.ctx),
    /** Cancela um convite pendente. Fora da interface. */
    cancelInvite: async (memberId: Id) => (await import('./team')).cancelInvite(this.ctx, memberId),
  };
}
